import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import darDeleteIcon from '../../../Assets/images/svg/dar_delete.svg';
import darDownloadIcon from '../../../Assets/images/svg/dar_download.svg';
import darEditIcon from '../../../Assets/images/svg/dar_edit.svg';
import darAddIcon from '../../../Assets/images/svg/dar_add.svg';
import lottie from 'lottie-web';
import emptyStateAnimationData from '../../../Assets/images/json/dar_no_data.json';
import { BillingReportAmountMethodDialog } from './BillingReportDialogs';

export const getNumericValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const numericValue = Number(`${value ?? ''}`.replace(/,/g, ''));
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const BillingReportEmptyState = () => {
  const animationContainer = useRef(null);
  useEffect(() => {
    if (!animationContainer.current) return undefined;
    const animation = lottie.loadAnimation({
      container: animationContainer.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: emptyStateAnimationData,
    });
    return () => animation.destroy();
  }, []);
  return (
    <div className="billing-report-empty-state-outer">
      <div className="billing-report-empty-state">
        <div ref={animationContainer} className="billing-report-empty-state__animation" />
        <p className="billing-report-empty-state__text">Select the filters above to see the results</p>
      </div>
    </div>
  );
};

export const BillingReportProjectTitle = ({ projectName, onProjectClick }) => (
  <span className="billing-report-heading__title">
    Billing Report{projectName ? ': ' : ''}
    {projectName ? (
      <button
        type="button"
        className="billing-report-project-link"
        onClick={onProjectClick}
        title="Open Time Sheet MIS"
      >
        {projectName}
      </button>
    ) : null}
  </span>
);

BillingReportProjectTitle.propTypes = {
  projectName: PropTypes.string,
  onProjectClick: PropTypes.func.isRequired,
};

export const BillingReportToolbarHeading = ({ projectName, dateRange, onProjectClick }) => (
  <span className="billing-report-heading">
    <BillingReportProjectTitle
      projectName={projectName}
      onProjectClick={onProjectClick}
    />
    {dateRange ? (
      <span className="billing-report-heading__date">Date : {dateRange}</span>
    ) : null}
  </span>
);

BillingReportToolbarHeading.propTypes = {
  projectName: PropTypes.string,
  dateRange: PropTypes.string,
  onProjectClick: PropTypes.func.isRequired,
};

export const BillingReportActions = ({
  row,
  disabled,
  deleting,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}) => (
  <div className="billing-report-actions">
    {canEdit ? (
      <button
        type="button"
        aria-label={`Edit billing report for ${row.employeeId}`}
        title="Edit"
        disabled={disabled}
        onClick={() => onEdit(row)}
      >
        <img src={darEditIcon} alt="" />
      </button>
    ) : null}
    {canDelete ? (
      <button
        type="button"
        aria-label="Delete billing report"
        title={row.canDelete
          ? 'Delete newly added employee'
          : 'Only newly added employees with zero logged effort can be deleted'}
        disabled={disabled || deleting || !row.canDelete}
        onClick={() => onDelete(row)}
      >
        <img src={darDeleteIcon} alt="" />
      </button>
    ) : null}
  </div>
);

BillingReportActions.propTypes = {
  row: PropTypes.shape({
    employeeId: PropTypes.string,
    canDelete: PropTypes.bool,
  }).isRequired,
  disabled: PropTypes.bool,
  deleting: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

BillingReportActions.defaultProps = {
  canEdit: false,
  canDelete: false,
};

export const BillingReportMoreMenu = ({
  selectedCount,
  disabled,
  onEdit,
  onTimesheet,
  onApprovalHistory,
  onUpload,
  canEdit,
  canCreate,
  showApprovalHistory,
  showAmount,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAmountMethodDialogOpen, setIsAmountMethodDialogOpen] = useState(false);
  const [selectedAmountMethod, setSelectedAmountMethod] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isOpen]);

  const handleTimesheetClick = () => {
    setIsOpen(false);
    onTimesheet();
  };

  const handleEditClick = () => {
    if (disabled || !selectedCount) return;
    setIsOpen(false);
    onEdit();
  };

  return (
    <div ref={menuRef} className="billing-report-more-menu">
      <button
        type="button"
        className="billing-report-more-menu__button"
        aria-label="More billing report options"
        aria-expanded={isOpen}
        title="More options"
        onClick={() => setIsOpen((previous) => !previous)}
      >
        <i className="pi pi-ellipsis-v" aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="billing-report-more-menu__panel">
          <button type="button" onClick={handleTimesheetClick}>
            Timesheet MIS
          </button>
          {showApprovalHistory ? (
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onApprovalHistory();
              }}
            >
              Approval History
            </button>
          ) : null}
          {canCreate ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                setIsOpen(false);
                onUpload();
              }}
            >
              Upload Billing Report
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              disabled={disabled || !selectedCount}
              onClick={handleEditClick}
            >
              Edit
            </button>
          ) : null}
          {showAmount ? (
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setIsAmountMethodDialogOpen(true);
              }}
            >
              Amount
            </button>
          ) : null}
        </div>
      )}
      {showAmount ? (
        <BillingReportAmountMethodDialog
          open={isAmountMethodDialogOpen}
          selectedMethod={selectedAmountMethod}
          onClose={() => setIsAmountMethodDialogOpen(false)}
          onSave={setSelectedAmountMethod}
        />
      ) : null}
    </div>
  );
};

BillingReportMoreMenu.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onTimesheet: PropTypes.func.isRequired,
  onApprovalHistory: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  canCreate: PropTypes.bool,
  showApprovalHistory: PropTypes.bool,
  showAmount: PropTypes.bool,
};

BillingReportMoreMenu.defaultProps = {
  disabled: false,
  canEdit: false,
  canCreate: false,
  showApprovalHistory: false,
  showAmount: false,
};

export const BillingReportToolbarControls = ({
  selectedCount,
  disabled,
  onEdit,
  onTimesheet,
  onApprovalHistory,
  onUpload,
  onAdd,
  canEdit,
  canCreate,
  showApprovalHistory,
  showAmount,
}) => (
  <>
    <BillingReportMoreMenu
      selectedCount={selectedCount}
      disabled={disabled}
      onEdit={onEdit}
      onTimesheet={onTimesheet}
      onApprovalHistory={onApprovalHistory}
      onUpload={onUpload}
      canEdit={canEdit}
      canCreate={canCreate}
      showApprovalHistory={showApprovalHistory}
      showAmount={showAmount}
    />
    {canCreate ? <button
      type="button"
      className="billing-report-add-button"
      aria-label="Add billing report"
      title="Add billing report"
      disabled={disabled}
      onClick={onAdd}
    >
      <img src={darAddIcon} alt="" aria-hidden="true" />
    </button> : null}
  </>
);

BillingReportToolbarControls.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onTimesheet: PropTypes.func.isRequired,
  onApprovalHistory: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  canCreate: PropTypes.bool,
  showApprovalHistory: PropTypes.bool,
  showAmount: PropTypes.bool,
};

BillingReportToolbarControls.defaultProps = {
  canEdit: false,
  canCreate: false,
  showApprovalHistory: false,
  showAmount: false,
};

export const BillingReportUploadDialog = ({
  fileName,
  uploading,
  onClose,
  onDownloadSample,
  onFileChange,
  onUpload,
}) => {
  const fileInputRef = useRef(null);

  return (
    <div className="billing-report-dialog-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !uploading) onClose();
    }}>
      <div
        className="billing-report-upload-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-report-upload-title"
      >
        <div className="billing-report-upload-dialog__header">
          <h2 id="billing-report-upload-title">Upload Billing Report</h2>
          <button type="button" onClick={onDownloadSample}>
            Sample File
          </button>
        </div>
        <div className="billing-report-upload-dialog__body">
          <label className="billing-report-upload-dialog__field">
            <span>Select file to upload<span className="billing-report-upload-dialog__required">*</span></span>
            <span className="billing-report-upload-dialog__file-row">
              <button type="button" onClick={() => fileInputRef.current?.click()}>
                Choose file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv"
                onChange={onFileChange}
              />
              <span>{fileName || 'No file chosen'}</span>
            </span>
          </label>
          <p className="billing-report-upload-dialog__hint">
            Accepted formats: Excel (.xlsx) and CSV. Upload rows must match the selected project and date.
          </p>
        </div>
        <div className="billing-report-upload-dialog__footer">
          <button type="button" onClick={onClose} disabled={uploading}>Cancel</button>
          <button type="button" onClick={onUpload} disabled={uploading || !fileName}>
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

BillingReportUploadDialog.propTypes = {
  fileName: PropTypes.string,
  uploading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onDownloadSample: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};

BillingReportUploadDialog.defaultProps = {
  fileName: '',
  uploading: false,
};

export const BillingReportEditDialog = ({
  rows,
  canViewAmount,
  canEditAmount,
  onClose,
  onSave,
}) => {
  const [editedRows, setEditedRows] = useState(() => rows.map((row) => ({
    ...row,
    billedEffort: `${row.billedEffort ?? row.loggedEffort ?? ''}`,
    totalAmountInr: `${row.totalAmountInr ?? ''}`,
  })));

  const updateRowValue = (employeeId, field, value) => {
    setEditedRows((currentRows) => currentRows.map((row) => (
      row.employeeId === employeeId ? { ...row, [field]: value } : row
    )));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await onSave(editedRows.map((row) => ({
      ...row,
      billedEffort: Number(row.billedEffort),
      ...(canEditAmount ? { totalAmountInr: Number(row.totalAmountInr) } : {}),
    })));
  };

  return (
    <div className="billing-report-dialog-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <form
        className="billing-report-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-report-edit-title"
        onSubmit={handleSubmit}
      >
        <div className="billing-report-edit-modal__header">
          <h2 id="billing-report-edit-title">Edit Report</h2>
        </div>
        <div className="billing-report-edit-modal__body">
          <div className="billing-report-edit-card">
            <table className="billing-report-edit-grid">
              <thead>
                <tr>
                  <th className="text-left col-emp-id">Employee Id</th>
                  <th className="text-left col-emp-name">Employee Name</th>
                  <th className="text-right col-logged-effort">Logged Effort</th>
                  <th className="text-right col-billed-effort">Billed Effort</th>
                  {canViewAmount && (
                    <th className="text-right col-total-amount">Total Amount (INR)</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {editedRows.map((row) => (
                  <tr key={row.employeeId}>
                    <td className="text-left col-emp-id">{row.employeeId}</td>
                    <td className="text-left col-emp-name">{row.employeeName}</td>
                    <td className="text-right col-logged-effort">{row.loggedEffort}</td>
                    <td className="text-right col-billed-effort">
                      <input
                        aria-label={`Billed effort for ${row.employeeId}`}
                        type="number"
                        min="0"
                        step="any"
                        className="billing-report-edit-input"
                        value={row.billedEffort}
                        onChange={(event) => updateRowValue(row.employeeId, 'billedEffort', event.target.value)}
                        required
                      />
                    </td>
                    {canViewAmount && (
                      <td className="text-right col-total-amount">
                        {canEditAmount ? (
                          <input
                            aria-label={`Total amount in INR for ${row.employeeId}`}
                            type="number"
                            min="0"
                            step="any"
                            className="billing-report-edit-input"
                            value={row.totalAmountInr}
                            onChange={(event) => updateRowValue(row.employeeId, 'totalAmountInr', event.target.value)}
                            required
                          />
                        ) : row.totalAmountInr}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="billing-report-edit-modal__footer">
          <button type="button" className="billing-report-edit-modal__cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="billing-report-edit-modal__save">
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

BillingReportEditDialog.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    employeeId: PropTypes.string.isRequired,
    employeeName: PropTypes.string,
    loggedEffort: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    billedEffort: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalAmountInr: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
  canViewAmount: PropTypes.bool,
  canEditAmount: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

BillingReportEditDialog.defaultProps = {
  canViewAmount: false,
  canEditAmount: false,
};

const downloadBillingReportCsv = (rows, projectName) => {
  const columns = [
    ['Employee ID', 'employeeId'],
    ['Employee Name', 'employeeName'],
    ['Logged Effort', 'loggedEffort'],
    ['Billed Effort', 'billedEffort'],
    ['Total Amount (INR)', 'totalAmountInr'],
  ];
  const escapeCsvValue = (value) => `"${`${value ?? ''}`.replace(/"/g, '""')}"`;
  const csv = [
    columns.map(([header]) => escapeCsvValue(header)).join(','),
    ...rows.map((row) => columns.map(([, field]) => escapeCsvValue(row[field])).join(',')),
  ].join('\r\n');
  const fileNameProject = projectName ? `_${projectName.replace(/[<>:"/\\|?*]+/g, '_')}` : '';
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  downloadLink.download = `Billing_Report${fileNameProject}.csv`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  URL.revokeObjectURL(url);
};

export const BillingReportCsvExportButton = ({ rowsRef, projectName, disabled }) => (
  <button
    type="button"
    className="billing-report-toolbar-button billing-report-toolbar-button--export"
    aria-label="Export billing report as CSV"
    title="Export CSV"
    disabled={disabled}
    onClick={() => {
      if (rowsRef.current.length) downloadBillingReportCsv(rowsRef.current, projectName);
    }}
  >
    <img src={darDownloadIcon} alt="" />
  </button>
);

BillingReportCsvExportButton.propTypes = {
  rowsRef: PropTypes.shape({ current: PropTypes.array }).isRequired,
  projectName: PropTypes.string,
  disabled: PropTypes.bool,
};

export const BillingReportFooter = ({ onCancel, onSubmit, disabled, submitting, submitted }) => (
  <div className="billing-report-footer">
    <button
      type="button"
      className="billing-report-footer__cancel"
      onClick={onCancel}
    >
      Cancel
    </button>
    <button
      type="button"
      className="billing-report-footer__submit"
      onClick={onSubmit}
      disabled={disabled || submitting || submitted}
    >
      Submit
    </button>
  </div>
);

BillingReportFooter.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  submitting: PropTypes.bool,
  submitted: PropTypes.bool,
};
